import { useState } from 'react';
import { BackHeader, useBackHandler } from '@/design-system';
import SpaceY from '../../../../components/SpaceY';
import type { SearchQuery } from '../../../../type/recipe';
import { GNB_HEIGHT } from '../../../../utils/common/gnbHeight';
import { Z_INDEX } from '../../../../utils/common/zIndex';
import {
  useGetThisWeekVegetables,
  usePushToRecipeQuery,
} from '../../../../utils/hook/recipe';
import useBandBanner from '../../../../utils/hook/useBandBanner';

import IngredientSearchButtons from './IngredientSearchButtons';
import RecipeSearchBar from './RecipeSearchBar';
import RecipeSearchFilter from './RecipeSearchFilter';

const RecipeSearchPopup = () => {
  const [queries, setQueries] = useState<SearchQuery>({
    q: '',
    ingredients: [],
    hashTags: [],
    time: undefined,
    isOfficialOnly: false,
    pageSize: 24,
  });

  const { thisWeekVegetables } = useGetThisWeekVegetables();
  const handleBack = useBackHandler({ fallbackPath: '/recipes' });
  const { searchWithQuery, handleSetFilter } = usePushToRecipeQuery(queries);
  const { bannerHeight } = useBandBanner();

  /**
   * 이 팝업 위에 그려지는 띠배너와 GNB를 합한 높이. 띠배너가 떠 있으면 GNB가 그만큼 내려와 있다.
   * 팝업은 모바일에서만 뜨므로 모바일 배너 높이를 쓴다.
   */
  const bannerWithGnbHeight = `calc(${GNB_HEIGHT} + ${bannerHeight.mobile}px)`;

  return (
    <div
      data-testid="recipe-search-popup"
      className="fixed inset-0 overflow-auto bg-white"
      style={{ zIndex: 1000 }}
    >
      {/* 흐름 밖(fixed)이라 자리를 자동으로 비워주지 않는다. 위에 그려지는 만큼 빈 요소로 채운다 */}
      <div
        className="w-full shrink-0"
        style={{ height: bannerWithGnbHeight }}
      />
      {/*
       * 헤더가 붙는 자리도 같은 값이라야 한다 — BackHeader가 가진 기본 자리(GNB 높이)는
       * 띠배너를 세지 않아 배너가 떠 있으면 헤더가 GNB에 덮인다.
       */}
      <div
        className="sticky"
        style={{ top: bannerWithGnbHeight, zIndex: Z_INDEX.BTB }}
      >
        <BackHeader title="통합 검색" onBack={handleBack} size="sm" />
      </div>
      <div className="px-16px">
        <RecipeSearchBar
          value={queries.q}
          onChange={(e) =>
            setQueries((prev) => ({ ...prev, q: e.target.value }))
          }
          onSearch={(keyword) => searchWithQuery()}
        />
        <SpaceY height="16px" />
        <IngredientSearchButtons />
      </div>
      <SpaceY height="32px" />
      <div className="px-16px">
        <RecipeSearchFilter
          thisWeekVegetables={thisWeekVegetables}
          setFilter={(filter) => setQueries((prev) => ({ ...prev, ...filter }))}
          onClickButton={(filter) => handleSetFilter(filter)}
        />
      </div>
      <SpaceY height="52px" />
    </div>
  );
};

export default RecipeSearchPopup;
